import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, Lock, UserCircle, ChalkboardTeacher, Info } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState("ortu"); // "ortu" | "guru"
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const switchRole = (r) => { setRole(r); setIdentifier(""); setPassword(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(identifier.trim(), password);
      toast.success("Assalamu'alaikum, selamat datang!");
      navigate(data.role === "admin" ? "/tk/admin/dashboard" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isGuru = role === "guru";

  return (
    <div className="min-h-screen app-bg flex justify-center">
      <div className="w-full md:max-w-md min-h-screen flex flex-col px-6 py-10">
        <div className="flex flex-col items-center mt-6 mb-6">
          <img src="/tk_logo.png" alt="TK Muslim Kids" className="w-24 h-24 object-contain" />
          <h1 className="font-heading text-2xl font-bold text-stone-800 mt-4">Masuk Akun</h1>
          <p className="text-stone-500 text-sm">Silakan pilih jenis akun Anda</p>
        </div>

        {/* Role toggle */}
        <div className="flex gap-2 bg-white border border-stone-200/60 rounded-2xl p-1 shadow-sm mb-6">
          <button data-testid="role-ortu" onClick={() => switchRole("ortu")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${!isGuru ? "bg-primary text-white shadow" : "text-stone-500"}`}>
            <UserCircle size={17} weight={!isGuru ? "fill" : "regular"} /> Orang Tua
          </button>
          <button data-testid="role-guru" onClick={() => switchRole("guru")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${isGuru ? "bg-primary text-white shadow" : "text-stone-500"}`}>
            <ChalkboardTeacher size={17} weight={isGuru ? "fill" : "regular"} /> Guru
          </button>
        </div>

        <motion.form key={role} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="login-email-input"
              type="text"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={isGuru ? "Nomor HP" : "Nomor Siswa"}
              className="w-full bg-white border border-stone-200 rounded-2xl py-4 pl-12 pr-4 outline-none focus:border-primary transition-colors"
            />
          </div>
          <div className="relative">
            <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Kata sandi"
              className="w-full bg-white border border-stone-200 rounded-2xl py-4 pl-12 pr-4 outline-none focus:border-primary transition-colors"
            />
          </div>

          {error && <p data-testid="login-error" className="text-destructive text-sm text-center">{error}</p>}

          <button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-primary text-white font-heading font-semibold text-lg py-4 rounded-full shadow-lg shadow-primary/20 active:scale-95 transition-transform disabled:opacity-60"
          >
            {loading ? "Memproses..." : isGuru ? "Masuk sebagai Guru" : "Masuk sebagai Orang Tua"}
          </button>
        </motion.form>

        {!isGuru ? (
          <>
            <p className="text-center mt-6">
              <Link to="/lupa-password" className="text-stone-500 text-sm font-semibold hover:text-primary" data-testid="goto-forgot-link">Lupa Password?</Link>
            </p>
          </>
        ) : (
          <div data-testid="guru-forgot-note" className="mt-6 flex items-center justify-center gap-1.5 text-stone-500 text-sm bg-white/70 border border-stone-200 rounded-2xl py-3 px-4">
            <Info size={16} weight="fill" className="text-primary shrink-0" />
            Lupa Password? Hubungi Admin
          </div>
        )}
      </div>
    </div>
  );
}
